import { GableChatMessage } from '../../../services/gable/gable-drawer.service';
import { LocalService } from '../../../services/storage/local.service';

/**
 * Feedback campaign: for 30 days Gable asks for a thumbs rating on more of its
 * answers than the permanent SD-10800 triggers (a decline, the reply after a
 * confirmed write). After this date only those triggers remain.
 */
export const FEEDBACK_CAMPAIGN_ENDS_AT = Date.parse('2026-11-20T00:00:00Z');

/** Ask on the 1st, 4th and 7th substantive answer of a conversation, never more. */
const ASK_EVERY = 3;
const MAX_ASKS_PER_CONVERSATION = 3;
/** An answer without tool calls counts as substantive from this length; shorter ones are chit-chat. */
const SUBSTANTIVE_CHARS = 280;
/** A vote on either of the two answers before this one means the reader already told us. */
const RECENT_VOTE_WINDOW = 2;

/** Asks ignored in a row before the campaign stops asking this person for a while. */
const IGNORED_LIMIT = 3;
const PAUSE_MS = 7 * 24 * 60 * 60 * 1000;

export function isSubstantiveAnswer(msg: GableChatMessage): boolean {
    const usedTools = (msg.toolsUsed?.length ?? 0) > 0
        || (msg.toolCards?.length ?? 0) > 0
        || (msg.thoughts ?? []).some((t) => (t.kind ?? 'tool') === 'tool');
    return usedTools || (msg.content ?? '').trim().length >= SUBSTANTIVE_CHARS;
}

/** Whether the campaign may ask at all: before its end date, and never for support staff impersonating someone. */
export function feedbackCampaignOn(now: number, impersonating: boolean): boolean {
    return now < FEEDBACK_CAMPAIGN_ENDS_AT && !impersonating;
}

/**
 * Changes whenever which answers ask can change. Answers above the last one
 * are finished. The last one never asks while it streams, so its text arriving
 * does not count; once complete, a server repair of its text or cards does.
 */
export function threadKey(messages: ReadonlyArray<GableChatMessage>): string {
    const last = messages[messages.length - 1];
    const settled = last?.streamState === 'complete'
        ? [last.content?.length ?? 0, last.toolCards?.length ?? 0, last.toolsUsed?.length ?? 0, last.thoughts?.length ?? 0]
        : [];
    return [messages.length, last?.streamState ?? '', last?.interactionId ?? '', last?.messageId ?? '', ...settled].join('|');
}

/** `permanent`: the SD-10800 asks (a decline, the reply after a confirmed write). `campaign`: the 30-day sample. */
export type FeedbackAskKind = 'permanent' | 'campaign';

export interface FeedbackAskInputs {
    messages: ReadonlyArray<GableChatMessage>;
    /** A vote, or reasons already sent: either way the reader has told us. */
    rated: (msg: GableChatMessage) => boolean;
    /** The SD-10800 triggers for the answer at `index`. */
    permanentAsk: (msg: GableChatMessage, index: number) => boolean;
    /** Campaign running for this person: not over, not impersonating, not paused. */
    campaignOn: boolean;
}

/**
 * Which finished answers ask for a rating, keyed by interaction id. One pass
 * over the thread, run when the thread or a vote changes, never per check.
 */
export function feedbackAsks({ messages, rated, permanentAsk, campaignOn }: FeedbackAskInputs): Map<number, FeedbackAskKind> {
    const asks = new Map<number, FeedbackAskKind>();
    const latest = messages.map((m) => m.role).lastIndexOf('assistant');
    const recent: GableChatMessage[] = [];
    let ordinal = 0;
    messages.forEach((msg, index) => {
        if (msg.role !== 'assistant') {
            return;
        }
        const substantive = isSubstantiveAnswer(msg);
        const recentlyRated = recent.slice(-RECENT_VOTE_WINDOW).some(rated);
        recent.push(msg);
        const ordinalBefore = ordinal;
        if (substantive) {
            ordinal += 1;
        }
        if (msg.streamState !== 'complete' || msg.interactionId === undefined || rated(msg)) {
            return;
        }
        if (permanentAsk(msg, index)) {
            asks.set(msg.interactionId, 'permanent');
        } else if (campaignOn && substantive && index === latest && !recentlyRated
            && ordinalBefore % ASK_EVERY === 0 && ordinalBefore / ASK_EVERY < MAX_ASKS_PER_CONVERSATION) {
            asks.set(msg.interactionId, 'campaign');
        }
    });
    return asks;
}

interface AskMemoryState {
    ignored: number;
    pausedUntil: number;
}

/**
 * Per-person memory of how the asks land, so someone who keeps scrolling past
 * them stops seeing them. Lives in localStorage keyed by user id; a blocked or
 * empty storage just means the campaign behaves as if this is a first visit.
 */
export class GableFeedbackAskMemory {
    private _state: AskMemoryState;

    constructor(private readonly _key: string | null) {
        this._state = this._read();
    }

    isPaused(now: number): boolean {
        return now < this._state.pausedUntil;
    }

    noteIgnored(now: number): void {
        const ignored = this._state.ignored + 1;
        this._write(ignored >= IGNORED_LIMIT
            ? { ignored: 0, pausedUntil: now + PAUSE_MS }
            : { ...this._state, ignored });
    }

    noteVoted(): void {
        if (this._state.ignored !== 0) {
            this._write({ ...this._state, ignored: 0 });
        }
    }

    private _read(): AskMemoryState {
        const empty = { ignored: 0, pausedUntil: 0 };
        if (!this._key) {
            return empty;
        }
        try {
            const parsed = JSON.parse(LocalService.get(this._key) ?? 'null');
            return {
                ignored: Number(parsed?.ignored) || 0,
                pausedUntil: Number(parsed?.pausedUntil) || 0,
            };
        } catch {
            return empty;
        }
    }

    private _write(state: AskMemoryState): void {
        this._state = state;
        if (!this._key) {
            return;
        }
        try {
            LocalService.set(this._key, JSON.stringify(state));
        } catch {
            // Storage blocked: the in-memory state still holds for this session.
        }
    }
}
